// src/App.tsx
import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "react-hot-toast";
import { SessionContextProvider, useSession } from "@supabase/auth-helpers-react";
import { supabase } from "./lib/supabaseClient";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Navbar from "./components/Navbar";
import Home from "./components/sections/Home";
import About from "./components/sections/About";
import Services from "./components/sections/Services";
import Menu from "./components/sections/Menu";
import Contact from "./components/sections/Contact";
import Cart from "./components/sections/Cart";
import SignIn from "./components/auth/SignIn";
import SignUp from "./components/auth/SignUp";
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";
import AuthCallback from "./components/auth/AuthCallback";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminDashboard from "./components/admin/AdminDashboard";
import UserDashboard from "./components/dashboard/UserDashboard";
import DineIn from "./components/services/dinein/dinein";
import Takeaway from "./components/services/takeaway/takeaway";
import PartyOrder from "./components/services/partyorder/partyorder";
import PlaceOrder from "./components/services/partyorder/placeorder";
import EventContact from "./components/services/partyorder/eventcontact";
import CheckoutForm from "./components/checkout/CheckoutForm";
import TakeawayMenu from "./components/services/takeaway/takeawaymenu";
import CompleteProfile from "./components/auth/CompleteProfile";
import Footer from "./components/Footer";
import RelocationModal from "./components/RelocationModal";
import PartyOrderMenu from "./components/services/partyorder/PartyOrderMenu";
import { ToastProvider } from "./components/toast";
import ScrollToTop from "./components/ScrollToTop";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

// Custom component to handle global ScrollTrigger
const ScrollTriggerWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  useEffect(() => {
    // Select all elements with the target class
    const elements = document.querySelectorAll(".scroll-trigger-ready__worm-wrap");

    elements.forEach((element) => {
      gsap
        .timeline({
          scrollTrigger: {
            trigger: element,
            scrub: 1,
            start: "top 90%",
            end: "bottom 30%",
          },
        })
        .to(element, {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
        });
    });

    // Refresh ScrollTrigger to account for dynamic content
    ScrollTrigger.refresh();

    // Cleanup on unmount or route change
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [location.pathname]); // Re-run effect when route changes

  return <>{children}</>;
};

const App = () => {
  return (
    <SessionContextProvider supabaseClient={supabase}>
      <Router>
        <ScrollToTop />
        <ThemeProvider>
          <AuthProvider>
            <CartProvider>
              <Toaster position="top-right" />
              <div className="flex flex-col min-h-screen">
                <Navbar />
                <main className="flex-grow pt-16">
                  <ScrollTriggerWrapper>
                    <Routes>
                      {/* Authentication Routes */}
                      <Route path="/signin" element={<SignIn />} />
                      <Route path="/signup" element={<SignUp />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route path="/complete-profile" element={<CompleteProfile />} />
                      <Route path="/toast" element={<ToastProvider />} />

                      {/* Public Pages */}
                      <Route path="/" element={<Home />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/services" element={<Services />} />
                      <Route path="/menu" element={<Menu />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/home" element={<Home />} />

                      {/* Services Pages */}
                      <Route path="/dine-in" element={<DineIn />} />
                      <Route path="/takeaway" element={<Takeaway />} />
                      <Route path="/party-order" element={<PartyOrder />} />

                      {/* Party Order Routes */}
                      <Route path="/services/partyorder/placeorder" element={<PlaceOrderWrapper />} />
                      <Route path="/services/party-order/menu" element={<PartyOrderMenu />} />
                      <Route path="/services/partyorder/eventcontact" element={<EventContact />} />

                      {/* Takeaway-Specific Routes */}
                      <Route path="/services/takeaway/menu" element={<TakeawayMenu />} />

                      {/* Cart and Checkout Routes */}
                      <Route
                        path="/cart"
                        element={
                          <ProtectedRoute>
                            <Cart />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/checkout"
                        element={
                          <ProtectedRoute>
                            <CheckoutForm />
                          </ProtectedRoute>
                        }
                      />

                      {/* Protected Routes */}
                      <Route
                        path="/admin/*"
                        element={
                          <ProtectedRoute>
                            <AdminDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/userdashboard"
                        element={
                          <ProtectedRoute>
                            <UserDashboard />
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </ScrollTriggerWrapper>
                </main>
                <Footer />
                <RelocationModal />
              </div>
            </CartProvider>
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </SessionContextProvider>
  );
};

// Wrapper component for protecting PlaceOrder route
const PlaceOrderWrapper = () => {
  const session = useSession();
  const userId = session?.user?.id || null;

  if (!userId) {
    return <p className="text-center text-red-500">You must be signed in to place an order.</p>;
  }

  return <PlaceOrder userId={userId} />;
};

export default App;